import { useEffect, useState } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  SafetyRiskAlertEventAttribute,
  SafetyRiskAlertEvents,
} from '~/analytics/safetyRiskAlertEvents';
import {
  basePatientTabsCollection,
  PatientTabs,
} from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { SafetyRiskAlertStatus } from '~/lib/types/survey/SafetyRiskAlert';
import { postAcknowledged } from '~/lib/util/safetyAlertBroadcast.util';
import { Path } from '~/routes/constants';
import {
  useActiveSafetyAlert,
  useSafetyAlertsDispatch,
} from '~/store/reducers/safetyAlerts';
import { useUpdateSafetyRiskAlertMutation } from '~/store/services/assessments';

import SafetyRiskAlertModal from './SafetyRiskAlertModal';

const SafetyRiskAlertContainer = () => {
  const { phq9Enabled } = useFlags<LDFlags>();
  const activeAlert = useActiveSafetyAlert();
  const { alertViewed, alertViewFailed, alertAcknowledged } = useSafetyAlertsDispatch();
  const { logError } = useDatadog();
  const { trackEvent } = useAnalytics();
  const [updateSafetyRiskAlert, { isLoading }] =
    useUpdateSafetyRiskAlertMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const trackAlertEvent = (status: SafetyRiskAlertStatus) => {
    const event =
      status === SafetyRiskAlertStatus.VIEWED
        ? SafetyRiskAlertEvents.safetyRiskAlertViewed
        : SafetyRiskAlertEvents.safetyRiskAlertAcknowledged;

    trackEvent(event, {
      [SafetyRiskAlertEventAttribute.AlertId]: activeAlert!.safetyRiskAlertId,
      [SafetyRiskAlertEventAttribute.TriggerReason]:
        activeAlert!.triggerReasonCode,
      [SafetyRiskAlertEventAttribute.PatientId]: activeAlert!.patientId,
      [SafetyRiskAlertEventAttribute.SurveyResponseExternalId]:
        activeAlert!.surveyResponseExternalId,
    });
  };

  useEffect(() => {
    if (!activeAlert) return;
    if (activeAlert.statusCode === SafetyRiskAlertStatus.VIEWED) return;

    alertViewed(activeAlert.safetyRiskAlertId);
    trackAlertEvent(SafetyRiskAlertStatus.VIEWED);
    
    updateSafetyRiskAlert({
      status: SafetyRiskAlertStatus.VIEWED,
      safetyRiskAlertId: activeAlert.safetyRiskAlertId,
    })
      .unwrap()
      .catch((error) => {
        alertViewFailed(activeAlert.safetyRiskAlertId);
        logError(error, { logFrom: 'Phq9 safety-alert mark-viewed' });
      });
  }, [activeAlert, updateSafetyRiskAlert, logError, trackEvent, alertViewed, alertViewFailed]);

  if (!phq9Enabled || !activeAlert) {
    return null;
  }

  const handleAcknowledge = async () => {
    setErrorMessage(null);
    try {
      await updateSafetyRiskAlert({
        status: SafetyRiskAlertStatus.ACKNOWLEDGED,
        safetyRiskAlertId: activeAlert.safetyRiskAlertId,
      }).unwrap();
      trackAlertEvent(SafetyRiskAlertStatus.ACKNOWLEDGED);
      postAcknowledged(activeAlert.safetyRiskAlertId);
      alertAcknowledged(activeAlert.safetyRiskAlertId);
      if (activeAlert.patientId) {
        const overviewPath =
          basePatientTabsCollection[PatientTabs.OVERVIEW].subPath;
        window.open(
          `${Path.MAUD_PATIENTS}/${activeAlert.patientId}/${overviewPath}`,
          '_blank',
          'noopener',
        );
      }
    } catch (error) {
      logError(error, { logFrom: 'Phq9 safety-alert ack' });
      setErrorMessage(
        'We could not record your acknowledgement. Please try again.',
      );
    }
  };

  return (
    <SafetyRiskAlertModal
      errorMessage={errorMessage}
      isSubmitting={isLoading}
      onAcknowledge={handleAcknowledge}
      triggerReasonCode={activeAlert.triggerReasonCode}
    />
  );
};

export default SafetyRiskAlertContainer;
